<template>
    <v-row>
        <v-col lg=12 sm=12 md=12 cols=12 class="px-0">
          <v-card outlined elevation="2" class="card-style">
            <v-card-title class="card-title"><p class="text-h5 text-truncate">{{ $t('component.stockChart.menu.title') }}</p>
              <v-spacer></v-spacer>
              <v-menu>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on">
                    <v-icon color="greyTint">mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(item, i) in optionsMenu"
                    :key="i"
                    @click="item.action">
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-card-title>
            <v-card-text>
                <v-col id="chart-container" lg=12 sm=12 md=12 cols=12 ref="chartContainer">
                    <div ref="chart" style="width: calc(100% + 33px); height: 450px;">
                        <v-skeleton-loader v-if="!(prices.length > 0)" class="height-450" type="image"></v-skeleton-loader>
                    </div>
                </v-col>
            </v-card-text>
          </v-card>
        </v-col>
        <ConfigurationModal :modalCode="modalCode" :chartOptions="chartOptions" @confirm="updateChartOptions"></ConfigurationModal>
    </v-row>
</template>

<script>  
    import { createChart } from 'lightweight-charts';
    import { mapState, mapActions } from 'vuex';

    import ConfigurationModal from '@/components/Charts/TradingView/ConfigurationModal';

    import { UtilsMixin } from '@/mixins/utils';

    export default {
        name: 'TradingViewChart',
        components: { ConfigurationModal },
        mixins: [ UtilsMixin ],
        props: {
            prices: Array,
            stopLossPrice: Number,
            breakoutPrice: Number,
            targetPrice: Number
        },
        data() {
            return {
                chart: null,
                chartOptions: {
                    showTarget: false,
                    showBreakout: false,
                    showStopLoss: false,
                    showSignalLine: true
                },
                candlestickSeries: null,
                volumeSeries: null,
                optionsMenu: [
                    { title: this.$t('component.stockChart.menu.fullscreen'), action: this.toggleFullscreen },
                    { title: this.$t('component.stockChart.menu.configuration'), action: this.showConfigurationModal }
                ],
                volumeGreen: '#1c5e5e',
                volumeRed: '#813439',
                modalCode: ''
            };
        },
        watch: {
            prices(newData, actualData) {
                // Se inicializa el gráfico de precios una vez se pasan los datos por primera vez al componente
                // (Luego el histórico no implica recargar el gráfico completo)
                if (newData.length > 0 && actualData.length === 0) { // Inicialización
                    this.createChart(); 
                } else if (actualData.length > 0) { // Carga de histórico
                    this.updateDailyDataOnChart(newData, actualData);
                }
            }
        },
        computed: {
            ...mapState('modal', ['modals']),
            stopLossPercentage () {
                return ' (-' + this.formatNumber((((this.breakoutPrice - this.stopLossPrice) / this.breakoutPrice) * 100), true) + '%)';
            },
            targetPricePercentage () {
                return ' (' + this.formatNumber((((this.targetPrice - this.breakoutPrice) / this.breakoutPrice) * 100), true) + '%)';
            }
        },
        methods: {
            ...mapActions('modal', ['showModal']),
            // Permite la configuración y creación de la tabla de precios
            createChart() {
                // Opciones del gráfico
                const chartOptions = {
                    layout: {
                        background: { type: 'solid', color: '#121212' }, // Fondo oscuro
                        textColor: '#E0E0E0',                            // Color de texto claro
                        fontSize: 12,                                    // Tamaño de fuente moderado
                        fontFamily: 'Roboto, sans-serif',                // Fuente materializada
                        borderColor: '#333333',                          // Color del borde (si aplica)
                    },
                    grid: {
                        vertLines: {
                            color: '#333333', // Líneas verticales grises
                        },
                        horzLines: {
                            color: '#333333', // Líneas horizontales grises
                        },
                    },
                    crosshair: {
                        mode: 0,              // Modo de crosshair
                        lineColor: '#FF5722', // Color de la línea de crosshair
                        width: 1,             // Ancho de la línea de crosshair
                    },
                    localization: {
                        timeFormatter: function(businessDayOrTimestamp) {
                            const date = new Date(businessDayOrTimestamp);
                            const day = date.getDate().toString().padStart(2, '0');          // Día con 2 dígitos
                            const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Mes con 2 dígitos
                            const year = (date.getFullYear()).toString().padStart(2, '0'); // Mes con 2 dígitos
                            return `${day}/${month}/${year}`;  
                        },
                    },
                    timeScale: {
                        borderColor: '#333333',// Borde de la escala de tiempo
                        rightOffset: 5,        // Offset para la visualización al lado derecho
                        visible: true,         // Mostrar la escala de tiempo
                        timeVisible: true,     // Muestra la escala de tiempo en vez de la de barras
                        secondsVisible: false, // No muestra segundos en el eje de tiempo
                        tickMarkFormatter: (timestamp) => {
                            const date = new Date(timestamp);
                            const day = date.getDate().toString().padStart(2, '0');          // Día con 2 dígitos
                            const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Mes con 2 dígitos
                            return `${day}/${month}`;                                        // Formato de día/mes
                        },
                    },
                    priceScale: {
                        borderColor: '#333333',    // Color del borde de la escala de precios
                        position: 'right',         // Colocar la escala de precios a la derecha
                        invertScale: false,        // No invierte la escala de precios
                        borderVisible: true,       // Muestra el borde de la escala de precios
                    },
                    watermark: {
                        color: 'rgba(0, 74, 173, 0.5)', // Marca de agua en color naranjo con transparencia
                        fontSize: 40,                   // Tamaño de la fuente
                        visible: true,                  // Habilitar la marca de agua
                        text: 'SignalView',             // Texto personalizado
                        font: 'Roboto',                 // Fuente de la marca de agua
                    },
                };

                // Crear el gráfico
                this.chart = createChart(this.$refs.chart, chartOptions);

                // Datos de precios
                this.candlestickSeries = this.chart.addCandlestickSeries();
                this.candlestickSeries.setData(this.prices);

                // Colocación del gráfico de velas inicialmente
                this.candlestickSeries.priceScale().applyOptions({
                    scaleMargins: {
                        top: 0.1,
                        bottom: 0.15,
                    },
                });

                // Ajuste del rango visible inicial
                const fromTime = this.prices[this.prices.length - 260]?.time || this.prices[0].time;
                const toTime = this.prices[this.prices.length - 1].time;

                // Establecer el rango visible en el gráfico
                this.chart.timeScale().setVisibleRange({
                    from: fromTime,
                    to: toTime,
                });

                // Margen derecho para que los datos no se inicialicen pegados a la barra de precios
                this.chart.applyOptions({ timeScale: { rightOffset: 15 } });

                // Opciones de las líneas de precio
                const lineWidth = 2;
                const minPriceLine = {
                    price: this.stopLossPrice,
                    color: '#FF5252', // Rojo para el precio mínimo
                    lineWidth: lineWidth,
                    lineStyle: 2, // Línea discontinua
                    axisLabelVisible: true, // Mostrar etiquetas del eje
                    title: (this.$t('component.stockChart.stoploss') + this.stopLossPercentage), // Título de la línea
                };
                const avgPriceLine = {
                    price: this.breakoutPrice,
                    color: '#B0BEC5', // Negro para el precio promedio
                    lineWidth: lineWidth,
                    lineStyle: 1, // Línea punteada
                    axisLabelVisible: true,
                    title: this.$t('component.stockChart.breakout'), // Título de la línea
                };
                const maxPriceLine = {
                    price: this.targetPrice,
                    color: '#66BB6A', // Verde para el precio máximo
                    lineWidth: lineWidth,
                    lineStyle: 2, // Línea discontinua
                    axisLabelVisible: true,
                    title: this.$t('component.stockChart.target') + this.targetPricePercentage, // Título de la línea
                };

                // Crear las líneas de precio en la serie
                if (this.chartOptions.showStopLoss) {
                    this.candlestickSeries.createPriceLine(minPriceLine);
                }
                if (this.chartOptions.showBreakout) {
                    this.candlestickSeries.createPriceLine(avgPriceLine);
                }
                if (this.chartOptions.showTarget) {
                    this.candlestickSeries.createPriceLine(maxPriceLine);
                }

                // Agregar la línea de precios de cierre
                if (this.chartOptions.showSignalLine) {
                    // Crear la línea que recorre los precios de cierre
                    const cupLineData = this.prices.map(data => {
                        let value;
                        // Condición para leftCup y rightCup
                        if (data.leftCup || data.rightCup) {
                            value = data.high;
                        }
                        // Condición para bottomCup
                        else if (data.bottomCup) {
                            value = data.low;
                        }
                        return { time: data.time, value };
                    });
                    const handleLineData = this.prices.map(data => {
                        let value;
                        // Condición para rightCup y lastValue
                        if (data.rightCup || data.lastValue) {
                            value = data.high;
                        }
                        // Condición para bottomCup
                        else if (data.handleBottom) {
                            value = data.low;
                        }
                        return { time: data.time, value };
                    });

                    const lineSeries = this.chart.addLineSeries({
                        color: '#FFB300', // Amarillo-anaranjado de Material Design
                        lineWidth: 2, // Ancho de la línea
                        lineStyle: 0, // Línea sólida
                        lineType: 2, // Efecto curvo
                        crosshairMarkerVisible: false, // No mostrar el marcador de precio al pasar el ratón
                        priceLineVisible: false, // No mostrar la línea de precio resaltado
                        tooltip: false, // Desactivar los tooltips de la serie
                        lastValueVisible: false // Evita que se muestre el tag en la columna de precios
                    });

                    lineSeries.setData(cupLineData);
                    const lineSeriesHandle = this.chart.addLineSeries({
                        color: '#FFB300', // Amarillo-anaranjado de Material Design
                        lineWidth: 2, // Ancho de la línea
                        lineStyle: 0, // Línea sólida
                        lineType: 2, // Efecto curvo
                        crosshairMarkerVisible: false, // No mostrar el marcador de precio al pasar el ratón
                        priceLineVisible: false, // No mostrar la línea de precio resaltado
                        tooltip: false, // Desactivar los tooltips de la serie
                        lastValueVisible: false // Evita que se muestre el tag en la columna de precios
                    });
                    lineSeriesHandle.setData(handleLineData);
                }

                // Mapear los datos para el volumen
                const volumeData = this.prices.map(data => {
                    return { time: data.time, value: data.volume, color: data.open > data.close ? this.volumeRed : this.volumeGreen };
                });

                // Añade una serie de histogramas para el volumen
                this.volumeSeries = this.chart.addHistogramSeries({
                    color: '#26a69a', // Color por defecto
                    lineWidth: 1,
                    priceScaleId: '', // Sin escala de precios (independiente)
                    overlay: true, // Muestra el volumen dentro del gráfico de precios
                    lastValueVisible: false // Evita que se muestre el tag con el valor del volumen
                });

                // Configura la colocación del volumen dentro del gráfico
                this.volumeSeries.priceScale().applyOptions({
                    scaleMargins: {
                        top: 0.8,
                        bottom: 0,
                    },
                });

                // Datos de ejemplo para la serie de volumen
                this.volumeSeries.setData(volumeData);

                // Configura leyenda con los precios del día seleccionado
                const legend = document.createElement('div');
                legend.style = `position: absolute; left: 18px; top: 18px; z-index: 1; font-size: 14px; font-family: sans-serif; line-height: 18px; font-weight: 300;`;
                legend.style.color = '#F5F5F5';
                document.getElementById('chart-container').appendChild(legend);

                const setTooltipHtml = (open, high, low, close, volume) => {
                    legend.innerHTML = `<div>O: ${open}&nbsp;&nbsp;H: ${high}&nbsp;&nbsp;L: ${low}&nbsp;&nbsp;C: ${close}<br>V: ${volume}</div>`;
                };

                const disableTooltipHtml = () => {
                    legend.innerHTML = ``;
                };

                const updateLegend = param => {
                    const validCrosshairPoint = !(
                        param === undefined || param.time === undefined || param.point.x < 0 || param.point.y < 0
                    );
                    const data = validCrosshairPoint ? param.seriesData.get(this.candlestickSeries) : null;
                    if (data) {
                        setTooltipHtml(this.formatNumber(data.open, false), 
                            this.formatNumber(data.high, false), 
                            this.formatNumber(data.low, false), 
                            this.formatNumber(data.close, false),
                            this.formatNumber(this.prices.find(i => i.time == data.time).volume), false);
                    } else {
                        disableTooltipHtml();
                    }
                };

                this.chart.subscribeCrosshairMove(updateLegend);

                // Añadir un evento para manejar el redimensionamiento de la ventana
                window.addEventListener('resize', () => {
                    const container = document.getElementById('chart-container');
                    if (container) {
                        const containerStyles = window.getComputedStyle(container);
                        const marginTop = parseInt(containerStyles.marginTop, 10);
                        const marginBottom = parseInt(containerStyles.marginBottom, 10);
                        const marginLeft = parseInt(containerStyles.marginLeft, 10);
                        const marginRight = parseInt(containerStyles.marginRight, 10);
                        const paddingTop = parseInt(containerStyles.paddingTop, 10);
                        const paddingBottom = parseInt(containerStyles.paddingBottom, 10);
                        const paddingLeft = parseInt(containerStyles.paddingLeft, 10);
                        const paddingRight = parseInt(containerStyles.paddingRight, 10);
                        const parentMarginBottom = 16;

                        const width = container.clientWidth - marginLeft - marginRight - paddingLeft - paddingRight;
                        const height = container.clientHeight - marginTop - marginBottom - paddingTop - paddingBottom - parentMarginBottom;

                        this.chart.resize(width, height);
                    }
                });

                document.addEventListener('fullscreenchange', () => {
                    if (document.fullscreenElement) {
                        this.chart.resize(window.innerWidth, window.innerHeight);
                    } else {
                        const container = document.getElementById('chart-container');
                        const containerStyles = window.getComputedStyle(container);
                        const marginTop = parseInt(containerStyles.marginTop, 10);
                        const marginBottom = parseInt(containerStyles.marginBottom, 10);
                        const marginLeft = parseInt(containerStyles.marginLeft, 10);
                        const marginRight = parseInt(containerStyles.marginRight, 10);
                        const paddingTop = parseInt(containerStyles.paddingTop, 10);
                        const paddingBottom = parseInt(containerStyles.paddingBottom, 10);
                        const paddingLeft = parseInt(containerStyles.paddingLeft, 10);
                        const paddingRight = parseInt(containerStyles.paddingRight, 10);
                        const parentMarginBottom = 16;

                        const width = container.clientWidth - marginLeft - marginRight - paddingLeft - paddingRight;
                        const height = container.clientHeight - marginTop - marginBottom - paddingTop - paddingBottom - parentMarginBottom;

                        this.chart.resize(width, height);
                    }
                });
            },
            // Añade el histórico de registro diarios en el gráfico
            updateDailyDataOnChart(newData) {
                const allDailyData = [...newData];
                this.candlestickSeries.setData(allDailyData);

                const updatedVolumeData = allDailyData.map(data => {
                    return { time: data.time, value: data.volume, color: data.open > data.close ? this.volumeRed : this.volumeGreen };
                });
                this.volumeSeries.setData(updatedVolumeData);
            },
            // Convierte el gráfico a pantalla completa
            toggleFullscreen() {
                const chartContainer = this.$refs.chartContainer;
                if (!document.fullscreenElement) {
                    chartContainer.requestFullscreen().then(() => {
                        this.chartInstance?.resize();

                        // Solicitar orientación horizontal en pantalla completa
                        if (this.isMobileDevice() && screen.orientation && screen.orientation.lock) {
                            screen.orientation.lock('landscape');
                        }
                    });
                } else {
                    document.exitFullscreen().then(() => {
                        this.chartInstance?.resize();

                        // Desbloquear orientación cuando salimos de pantalla completa
                        if (this.isMobileDevice() && screen.orientation && screen.orientation.unlock) {
                            screen.orientation.unlock();
                        }
                    });
                }
            },
            // Muestra modal de configuración del gráfico de precios
            showConfigurationModal() {
                this.modalCode = 'chartConfigurationModal';
                this.$store.dispatch('modal/showModal', {
                    id: this.modalCode,
                    payload: {
                        title: this.$t('general.configuration'), 
                        confirmButtonText: this.$t('general.ok'), 
                        cancelButtonText: this.$t('general.cancel')
                    },
                });
            },
            // Actualiza las opciones seleccionadas en el gráfico de precios
            updateChartOptions(values) {
                this.chart.remove();
                this.chartOptions = values;

                this.createChart();
            }
        },
    };
</script>

<style scoped>
    #chart-container {
        width: 100%; 
        height: 100%; 
        position: relative;
        padding: 3px !important;
        margin: 0 -16px -16px -16px !important;
    }

    .v-card__text {
        padding: 0 !important;
    }

    .card-title {
        margin-bottom: 0 !important;
    }
</style>